import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import PublicNav from "../components/PublicNav";
import PublicFooter from "../components/PublicFooter";
import PublicBottomNav from "../components/PublicBottomNav";
import { Helmet } from "react-helmet-async";
import { Button } from "../components/ui/button";
import JsonLd from "../components/JsonLd";
import PortalBottomNav from "../components/PortalBottomNav";
import { 
  ChevronDown, 
  ChevronUp, 
  HelpCircle, 
  Home, 
  Users, 
  Mountain,
  MapPin,
  Mic,
  MessageCircle,
  ArrowLeft
} from "lucide-react";
import { pic } from "../lib/pic";

const FAQPage = () => {
  const [openIndex, setOpenIndex] = useState(null);
  const [searchParams] = useSearchParams();
  const fromApp = searchParams.get("from") === "app";

  const faqs = [
    {
      question: "What's included in the monthly price?",
      answer: "The monthly fee includes your private room, access to all common spaces including our dedicated coworking space, ultra-fast Starlink WiFi, weekly cleaning, and fresh linens and towels. Utilities are also included."
    },
    {
      question: "Is Savi really a sober coliving space?",
      answer: "Yes. Savi is a 100% sober, smoke-free environment — no alcohol, no recreational drugs, no smoking. This is central to our community ethos. We welcome anyone who appreciates clarity, mindfulness, and authentic connection without substances."
    },
    {
      question: "What happens if someone is found with alcohol, or smoking, on site?",
      answer: "They leave the same day, and there are no refunds — not of rent, not of the deposit, not of unused nights. Savi is a sober house, not a rehab. We are not qualified to support anyone through addiction, and we won't put the rest of the house in that position. The same applies to smoking: any alcohol or smoking on site means immediate dismissal. If that's going to be a struggle, Savi isn't the right place for you right now — and that's okay."
    },
    {
      question: "What's the minimum stay?",
      answer: "We have a minimum stay of 1 month. Most guests stay between 1-3 months, though some have extended to 6 months or more. We prefer guests who want to settle in and become part of the community."
    },
    {
      question: "Can I bring my pet?",
      answer: "Yes, with prior approval. Savi has three resident dogs — Viki, Lluna, and Jenny — who roam freely outdoors. We welcome well-behaved dogs and cats. One firm rule: animals aren't allowed in the main house or the kitchen. Everywhere else on site is fine, and the whole property is fully fenced, so there's no chance of an escape. Standard pet fee: €50 per week per animal. Optional pet care (feeding, walking, general care while you're away from the property): €50 per day. Message Steve to confirm approval before booking."
    },
    {
      question: "How fast is the WiFi?",
      answer: "We use Starlink, which provides ultra-fast, reliable internet even in our mountain location. Video calls, streaming, and large file transfers are no problem. We're getting speeds of up to 500Mbps down."
    },
    {
      question: "What's the cancellation policy?",
      answer: "Plans change — here's how it works, based on how much notice you give before arrival:\n\n• More than 2 months: deposit refunded in full, minus a €100 admin fee.\n• 1–2 months: half the deposit refunded, minus a €100 admin fee.\n• Less than 1 month: the deposit is non-refundable.\n\nOnce you've arrived, unused nights aren't refundable, and changing your dates counts as a cancellation if we can't confirm the new ones. Life happens — for serious illness or genuine emergencies we'll always look at it case by case, though we can't guarantee a refund. Just message Steve in writing (email or WhatsApp); any refund is processed within 14 days.",
      link: "#cancellation-policy",
      linkText: "Full details on our Terms & Conditions →"
    },
    {
      question: "What's the food situation?",
      answer: "Mostly plant-based, but not strict — we eat minimal meat with the occasional fish or chicken. This is Spain after all… tuna is a vegetable. You're always welcome to cook your own meals with whatever you like; the guest kitchen is fully equipped."
    },
    {
      question: "How do I get to Savi from Valencia?",
      answer: "We're about 25 minutes from Valencia city center by car. If you're arriving by train or plane, the easiest option is a taxi or ride-share. We can help arrange airport pickup if needed. There's also infrequent bus service to Náquera."
    },
    {
      question: "Do I need a car?",
      answer: "Honestly, it helps. We're in the hills, five minutes' drive from Náquera, so most guests bring or hire a car or a scooter. We don't rent cars ourselves (we do have bikes) and we don't run a shopping shuttle. If you'd rather go without, that's fine — Consum and Mercadona deliver to the house. Housemates often head into town and are happy to take a passenger, but that's goodwill, not a guarantee."
    },
    {
      question: "Is there a workspace or coworking area?",
      answer: "Yes! We have a dedicated coworking space with external screens available, plus a terrace workspace with views over the Mediterranean Sea. You can also work from various cozy spots around the villa — the living room, covered terraces, or your private room."
    },
    {
      question: "What's the community vibe like?",
      answer: "We're a close-knit group of digital nomads, creatives, and remote workers from around the world. The vibe is warm, supportive, and low-key. Community meals are optional but encouraged. We value both connection and personal space."
    },
    {
      question: "Do you organize activities?",
      answer: "Yes! Steve organizes regular hikes, cultural trips to Valencia, biodescodification workshops, and occasional skill-shares. Guests often organize informal activities too — movie nights, sunset sessions, or group cooking."
    },
    {
      question: "What about quiet hours?",
      answer: "We observe quiet hours after 10pm. Since we're in the mountains surrounded by nature, most guests naturally wind down early. It's a peaceful environment for early risers and those who value restful sleep."
    }
  ];

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const faqSchemaData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(f => ({
      "@type": "Question",
      "name": f.question,
      "acceptedAnswer": { "@type": "Answer", "text": f.answer }
    }))
  };

  return (
    <div className="min-h-screen bg-[#FBF7F1]">
      <Helmet>
        <title>FAQ | Savi Coliving Valencia</title>
        <meta name="description" content="FAQ about Savi Coliving. Learn about bookings, WiFi speeds, the sober policy, what's included, and what to expect." />
        <meta property="og:title" content="FAQ | Savi Coliving" />
        <meta property="og:description" content="Everything you need to know about staying at Savi Coliving in Valencia, Spain." />
        <meta name="twitter:card" content="summary" />
        <link rel="canonical" href="https://www.savicoliving.com/faq" />
      </Helmet>
      <JsonLd id="faq" data={faqSchemaData} />

      {/* Public top + mobile nav — hidden when guest opened FAQ from inside the portal */}
      {!fromApp && (
        <>
          {/* Navigation */}
          <PublicNav />

          {/* Mobile Navigation */}
          <PublicBottomNav />
        </>
      )}

      {/* In-app top bar — keeps users inside the portal */}
      {fromApp && (
        <nav className="fixed top-0 left-0 right-0 z-50 bg-[#FBF7F1]/95 backdrop-blur-lg border-b border-stone-100">
          <div className="max-w-3xl mx-auto px-4 h-14 flex items-center justify-between">
            <Link
              to="/dashboard?tab=profile"
              className="flex items-center gap-2 text-[#2E2A26] text-sm font-medium hover:text-[#C85A38] transition-colors"
              data-testid="back-to-app-btn"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to the app
            </Link>
            <span className="font-serif text-[#2E2A26]">FAQ</span>
          </div>
        </nav>
      )}

      {/* Hero */}
      <header className="pt-16">
        <div className="h-[40vh] sm:h-[50vh] relative overflow-hidden">
          <img 
            {...pic("house-team")}
            fetchpriority="high" decoding="async"
            alt="FAQ"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#FBF7F1] from-15% via-[#FBF7F1]/75 via-50% to-black/20"></div>
          <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-8">
            <div className="max-w-6xl mx-auto">
              <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl text-[#2E2A26] mb-2">FAQ</h1>
              <p className="text-[#2E2A26]/70 text-sm sm:text-base">Everything you need to know about life at Savi</p>
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="pb-24 md:pb-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
          <div className="space-y-3">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className="bg-[#FBF7F1] rounded-xl sm:rounded-2xl border border-stone-100 overflow-hidden"
              >
                <button
                  onClick={() => toggleFAQ(index)}
                  className="w-full px-4 sm:px-6 py-4 flex items-center justify-between text-left"
                  data-testid={`faq-question-${index}`}
                >
                  <span className="font-medium text-[#2E2A26] pr-4 text-sm sm:text-base">{faq.question}</span>
                  {openIndex === index ? (
                    <ChevronUp className="w-5 h-5 text-[#C85A38] flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-[#2E2A26]/40 flex-shrink-0" />
                  )}
                </button>
                
                <div
                  className={`transition-all duration-300 ${
                    openIndex === index ? "max-h-[600px] opacity-100" : "max-h-0 opacity-0"
                  } overflow-hidden`}
                >
                  <div className="px-4 sm:px-6 pb-4 text-[#2E2A26]/70 text-sm sm:text-base leading-relaxed whitespace-pre-line">
                    {faq.answer}
                    {faq.link && (
                      <a
                        href={faq.link}
                        className="block mt-3 text-[#C85A38] hover:text-[#D06A4F] font-medium no-underline"
                        data-testid={`faq-link-${index}`}
                      >
                        {faq.linkText}
                      </a>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Still have questions */}
          <div className="mt-10 sm:mt-12 text-center bg-[#F4ECE2] rounded-2xl sm:rounded-3xl p-6 sm:p-8">
            <h3 className="font-serif text-xl sm:text-2xl text-[#2E2A26] mb-2">Still have questions?</h3>
            <p className="text-[#2E2A26]/60 mb-4 sm:mb-6 text-sm sm:text-base">
              Steve is happy to answer any questions you might have.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <a href="https://wa.me/34632628051" target="_blank" rel="noopener noreferrer">
                <Button className="bg-[#25D366] hover:bg-[#1da851] text-white rounded-full px-6 w-full sm:w-auto">
                  WhatsApp Steve
                </Button>
              </a>
              <Link to="/contact">
                <Button variant="outline" className="border-[#2E2A26] text-[#2E2A26] rounded-full px-6 w-full sm:w-auto">
                  Contact Form
                </Button>
              </Link>
            </div>
          </div>

          {/* Terms & Conditions */}
          <div className="mt-12 sm:mt-16">
            <h2 className="font-serif text-2xl sm:text-3xl text-[#2E2A26] mb-6">Terms & Conditions</h2>
            <div className="bg-white rounded-2xl p-6 sm:p-8 border border-stone-100 space-y-4 text-sm text-[#2E2A26]/70">
              <p><strong className="text-[#2E2A26]">1. Booking & Payment</strong><br />
              A 30% security deposit is required to secure the booking. The booking is not considered confirmed until this deposit has been received. The remaining rental balance must be paid in full 30 days before arrival.</p>
              
              <p><strong className="text-[#2E2A26]">2. Minimum Stay</strong><br />
              The minimum stay at Savi Coliving is 1 month. Extensions are subject to availability and must be requested at least 2 weeks in advance.</p>
              
              <p><strong className="text-[#2E2A26]">3. House Rules</strong><br />
              Savi is a 100% sober, smoke-free environment. No alcohol, recreational drugs or smoking are permitted anywhere on the property. Guests agree to respect quiet hours (10pm-8am), shared spaces, and fellow community members.</p>

              <p data-testid="terms-sober-dismissal"><strong className="text-[#2E2A26]">3a. Alcohol and smoking on site — immediate dismissal</strong><br />
              Any guest found with alcohol on site, or smoking on site, will be required to leave immediately — the same day. In these cases no refund of rent, deposit or unused nights will be given. Savi Coliving is not a rehabilitation facility and is not qualified to support guests with addiction or dependency. By booking, guests confirm they are able to live alcohol-free and smoke-free for the full duration of their stay.</p>
              
              <p><strong className="text-[#2E2A26]">4. Liability</strong><br />
              Savi Coliving is not responsible for loss, theft, or damage to personal belongings. Guests are encouraged to have personal travel insurance.</p>
              
              <p><strong className="text-[#2E2A26]">5. Check-in & Check-out</strong><br />
              Check-in is from 3pm onwards. Check-out is by 11am on your departure date. Early check-in or late check-out may be arranged subject to availability.</p>
            </div>
          </div>

          {/* Animal Policy */}
          <div className="mt-10 sm:mt-12">
            <h2 className="font-serif text-2xl sm:text-3xl text-[#2E2A26] mb-6">🐕 Animal Policy</h2>
            <div className="bg-white rounded-2xl p-6 sm:p-8 border border-stone-100 space-y-5 text-sm text-[#2E2A26]/70">
              <p>
                Guests are welcome to bring well-behaved dogs and cats with prior approval. Savi has three resident dogs on the property and a few simple rules to keep everyone safe and comfortable.
              </p>
              <div className="bg-[#F4ECE2] rounded-xl p-5 border border-stone-200 space-y-2" data-testid="faq-animal-where">
                <p className="text-[#2E2A26]">
                  <strong>Where animals can go:</strong> everywhere on site except the main house and the kitchen. Gardens, terraces, pool area, the lot.
                </p>
                <p className="text-[#2E2A26]">
                  <strong>Fully fenced:</strong> the whole property is enclosed, so there's no chance of an escape. Dogs can roam without you watching the gate.
                </p>
              </div>
              <div className="bg-[#FBF7F1] rounded-xl p-5 border border-stone-200 space-y-3">
                <p className="text-[#2E2A26]">
                  <strong>Standard pet fee:</strong> €50 per week per animal
                </p>
                <p className="text-[#2E2A26]">
                  <strong>Optional pet care service:</strong> €50 per day per animal (feeding, walking & general care while you're away from the property)
                </p>
                <p className="text-[#2E2A26]/50 text-xs italic">
                  These are separate charges. The pet fee applies for the duration of the stay; the pet care service is only charged on days the service is booked.
                </p>
              </div>
              <p>
                Pet approval, behaviour expectations and liability are agreed directly with Steve — just message him on WhatsApp before booking.
              </p>
            </div>
          </div>

          {/* Cancellation Policy */}
          <div className="mt-10 sm:mt-12 scroll-mt-24" id="cancellation-policy">
            <h2 className="font-serif text-2xl sm:text-3xl text-[#2E2A26] mb-6">Cancellation Policy</h2>
            <div className="bg-white rounded-2xl p-6 sm:p-8 border border-stone-100 space-y-4 text-sm text-[#2E2A26]/70">
              <p><strong className="text-[#2E2A26]">More than two months before arrival:</strong><br />
              100% of the deposit shall be refunded, minus a €100 administrative fee.</p>
              
              <p><strong className="text-[#2E2A26]">Between one and two months before arrival:</strong><br />
              50% of the deposit shall be refunded, minus a €100 administrative fee.</p>
              
              <p><strong className="text-[#2E2A26]">Within one month of arrival:</strong><br />
              The deposit is non-refundable.</p>
              
              <p><strong className="text-[#2E2A26]">Early departure:</strong><br />
              If the guest leaves before the agreed departure date, unused nights are non-refundable. This includes guests dismissed for alcohol or smoking on site (see House Rules, 3a): no rent, deposit or unused nights are refunded.</p>
              
              <p><strong className="text-[#2E2A26]">Date changes:</strong><br />
              Changing booking dates shall be treated as a cancellation if the new dates cannot be confirmed.</p>
              
              <p><strong className="text-[#2E2A26]">Exceptional circumstances:</strong><br />
              In exceptional circumstances, such as serious illness or emergency, Savi Coliving may consider the case individually, but no refund is guaranteed.</p>
              
              <p><strong className="text-[#2E2A26]">How to cancel:</strong><br />
              All cancellations must be communicated in writing by email or WhatsApp. Any applicable refund shall be processed within 14 days.</p>
              
              <p className="text-[#2E2A26]/50 italic pt-4 border-t border-stone-100">
                Last updated: January 2026. Savi Coliving reserves the right to update these terms at any time.
              </p>
            </div>
          </div>
        </div>
      </main>

      {/* Footer — hidden in-app */}
      {!fromApp && (
        <PublicFooter />
      )}

      {/* Portal bottom nav — keeps in-app users inside the portal */}
      {fromApp && <PortalBottomNav active="profile" />}
    </div>
  );
};

export default FAQPage;
